import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EmailThread } from '../../contexts/EmailInboxContext';
import { useEmailInbox, leadMessage } from '../../contexts/EmailInboxContext';
import { useEscalationContact } from '../../contexts/EscalationContactContext';
import { useProxyWorkflowContext } from '../../hooks/useProxyWorkflowContext';
import { buildProxyIntakeTasks } from '../../data/proxyWorkflowRegistry';
import { classifyEmail, EMAIL_CATEGORY_LABEL, type ProxyEmailCategory } from '../../utils/proxyEmailClassifier';

// Right-side slide-over, patterned directly on RegulatoryDetailDrawer.tsx —
// opens on row click, shows the full thread, and lets you reply. Reverse
// task-linkage ("which task did this create") deep-links to the Intake
// Queue with `?taskId=`, which scrolls to and highlights that row — task
// ids are deterministic (derived from job/email id), so they resolve
// without any backend persistence.
// Reply-to defaults: continue the conversation with whoever's on the other
// end of the last message (the sender if it was inbound, the recipient if
// Ace/You sent it last) and prefix the subject with "Re:" once, same as any
// mail client.
function defaultReplyTo(thread: EmailThread): string {
  const last = thread.messages[thread.messages.length - 1];
  if (!last) return '';
  return last.direction === 'inbound' ? last.fromEmail : last.to;
}

function defaultReplySubject(thread: EmailThread): string {
  return /^re:/i.test(thread.subject) ? thread.subject : `Re: ${thread.subject}`;
}

// '2026-10-05' -> 'Oct 5'
function formatDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function EmailInboxDetailDrawer({ thread, onClose }: { thread: EmailThread; onClose: () => void }) {
  const { threads, appendReply, appendOutbound, deleteThread, setThreadCategory } = useEmailInbox();
  // ICT-4: whether the Proxy/ICT manager is out comes from their
  // out-of-office on the Admin Dashboard.
  const { contact } = useEscalationContact();
  const managerOut = !!contact?.manager_out;
  const managerName = contact?.manager.name ?? 'The ICT manager';
  // `thread` is the row clicked; read the category off the live thread so a
  // change shows straight away.
  const live = threads.find(t => t.id === thread.id) ?? thread;
  const workflowCtx = useProxyWorkflowContext();
  const navigate = useNavigate();
  const [to, setTo] = useState(() => defaultReplyTo(thread));
  const [subject, setSubject] = useState(() => defaultReplySubject(thread));
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  // What Ace made of the original inbound email (ICT-4 routing).
  const original = leadMessage(thread);
  const ace = thread.origin === 'proxy' && original?.direction === 'inbound'
    ? classifyEmail({ ...original, id: original.id, subject: thread.subject })
    : null;

  const linkedTasks = buildProxyIntakeTasks(workflowCtx).filter(t =>
    thread.messages.some(m => t.sourceEmailId === m.id) || thread.jobNumbers.includes(t.jobId),
  );

  function handleReport() {
    if (managerOut) {
      appendOutbound({
        refKey: `report-${thread.id}`, jobIds: [], origin: 'proxy',
        to: 'ict-team@betanxt.com', subject: `Fwd: ${thread.subject}`,
        body: `Forwarding today's report while the ICT manager is out.\n\n${original?.body ?? ''}`,
      });
    }
    deleteThread(thread.id);
    onClose();
  }

  function handleSend() {
    if (!draft.trim()) return;
    setSending(true);
    const editedSubject = subject.trim();
    appendReply(thread.id, {
      from: 'You',
      fromEmail: 'ops@betanxt.com',
      to: to.trim(),
      subject: editedSubject && editedSubject !== defaultReplySubject(thread) ? editedSubject : undefined,
      body: draft.trim(),
    });
    setDraft('');
    setSending(false);
  }

  return (
    <div className="fixed inset-0 z-[200] flex justify-end" style={{ background: 'rgba(4,50,67,.5)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white h-full w-[420px] shadow-modal flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-trim flex-shrink-0">
          <div className="min-w-0">
            <div className="text-base font-semibold text-ink truncate">{thread.subject}</div>
            <div className="text-xs text-muted mt-0.5">{thread.origin === 'proxy' ? 'Proxy' : 'Fulfillment'}</div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-copy hover:bg-wash flex-shrink-0">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-b border-trim flex-shrink-0 flex-wrap">
          {thread.jobNumbers.length > 0 ? (
            thread.jobNumbers.map(id => (
              <span key={id} className="text-xs font-medium" style={{ color: '#2563EB' }}>Job # {id}</span>
            ))
          ) : (
            <span className="text-xs text-muted">No Job # linked</span>
          )}
          {linkedTasks.map((t, i) => (
            <button
              key={t.id}
              data-tour={i === 0 ? 'drawer-task-link' : undefined}
              onClick={() => { onClose(); navigate(`/proxy?taskId=${t.id}`); }}
              className="inline-flex items-center text-xs font-medium px-2 py-[3px] rounded-md bg-[#E9F6F6] text-[#063F5A] hover:bg-[#d9efef] transition-colors"
            >
              → Created task: {t.title}
            </button>
          ))}
        </div>

        {ace && (
          <div data-tour="email-ace-read" className="px-5 py-3 border-b border-trim flex-shrink-0 text-xs flex flex-col gap-1">
            <div className="text-ink"><span className="font-semibold">Ace:</span> {ace.why}</div>
            <div data-tour="email-category-change" className="flex items-center gap-2 flex-wrap">
              <span className="text-copy">Category</span>
              <select
                value={live.category ?? ''}
                onChange={e => setThreadCategory(thread.id, e.target.value as ProxyEmailCategory)}
                className="text-xs border border-trim rounded-md px-1.5 py-1 bg-white text-ink"
              >
                {!live.category && <option value="" disabled>Unclassified</option>}
                {(Object.keys(EMAIL_CATEGORY_LABEL) as ProxyEmailCategory[]).map(c => (
                  <option key={c} value={c}>{EMAIL_CATEGORY_LABEL[c]}</option>
                ))}
              </select>
              {live.correction && (
                <span className="text-muted">
                  Changed by {live.correction.by === 'You' ? 'you' : live.correction.by} from {live.correction.from ? EMAIL_CATEGORY_LABEL[live.correction.from] : 'Unclassified'}
                </span>
              )}
            </div>
            {ace.category === 'REPORT' ? (
              <div className="flex flex-col gap-2 mt-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-copy">
                    {managerOut
                      ? `${managerName} is out until ${formatDay(contact!.out_until!)}, so forward it to the team for distribution, then delete it (ICT-4).`
                      : `${managerName} is in, so it can be deleted (ICT-4).`}
                  </span>
                  <button onClick={handleReport} className="text-xs font-semibold text-white bg-[#063F5A] rounded-md px-2.5 py-1 whitespace-nowrap">
                    {managerOut ? 'Forward, then delete' : 'Delete'}
                  </button>
                </div>
                <span data-tour="drawer-manager-out" className="self-start text-muted">
                  Out of office is set on the Admin Dashboard.
                </span>
              </div>
            ) : (
              <div className="text-copy">Routed to {ace.routeTo}{ace.fileIn ? ` · filed in ${ace.fileIn}` : ''}</div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          {thread.messages.map(m => (
            <div key={m.id} className="rounded-lg border border-trim p-3">
              <div className="grid gap-1 mb-2 pb-2 border-b border-wash" style={{ gridTemplateColumns: '60px 1fr', fontSize: 13 }}>
                <span className="text-muted">From</span>
                <span className="text-ink">{m.sentByAce ? m.fromEmail : `${m.from} ${m.fromEmail ? `<${m.fromEmail}>` : ''}`}</span>
                {m.to && <><span className="text-muted">To</span><span className="text-ink">{m.to}</span></>}
                {m.cc.length > 0 && <><span className="text-muted">Cc</span><span className="text-ink">{m.cc.join(', ')}</span></>}
                {m.subject && <><span className="text-muted">Subject</span><span className="text-ink">{m.subject}</span></>}
                <span className="text-muted">Date</span>
                <span className="text-ink">{m.date}</span>
              </div>
              <div className="text-sm text-ink leading-relaxed whitespace-pre-line">{m.body}</div>
            </div>
          ))}
        </div>

        <div className="px-5 py-4 border-t border-trim flex-shrink-0 flex flex-col gap-2">
          <div className="rounded-lg border border-trim overflow-hidden focus-within:border-teal-400">
            <div className="grid items-center gap-2 px-3 py-1.5 border-b border-wash" style={{ gridTemplateColumns: '60px 1fr' }}>
              <span className="text-xs text-muted">To</span>
              <input
                value={to}
                onChange={e => setTo(e.target.value)}
                placeholder="Recipient email"
                className="w-full text-sm text-ink outline-none bg-transparent py-0.5"
              />
            </div>
            <div className="grid items-center gap-2 px-3 py-1.5" style={{ gridTemplateColumns: '60px 1fr' }}>
              <span className="text-xs text-muted">Subject</span>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Subject"
                className="w-full text-sm text-ink outline-none bg-transparent py-0.5"
              />
            </div>
          </div>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Write a reply…"
            rows={3}
            className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white resize-none"
          />
          <button
            onClick={handleSend}
            disabled={sending || !draft.trim()}
            className="self-start px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:shadow-md hover:shadow-teal-500/25 transition-all disabled:opacity-50"
          >
            Send reply
          </button>
        </div>
      </div>
    </div>
  );
}
